import React, { useState, createContext, useEffect, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import styles from './styles.css'
import { Form, Field } from 'simple-react-form'
import withSize from '@orionx-dev/orionsoft-parts/lib/decorators/withSize'
import moment from 'moment'
import { FaLock } from 'react-icons/fa'
import TwoFactorButton from 'App/components/fields/TwoFactorButton'
import PropTypes from 'prop-types'
import { Outlet } from 'react-router-dom'

const GET_MY_TWO_FACTOR_SETTINGS = gql`
  query getMyTwoFactorSettings {
    me {
      _id
      profile {
        fullName
      }
      hasTwoFactor
    }
  }
`

export const ConfirmContext = createContext({})

const ConfirmationDialogComponent = ({ children, innerWidth, ...props }) => {
  const { error, data } = useQuery(GET_MY_TWO_FACTOR_SETTINGS)

  const [isOpen, setIsOpen] = useState(false)
  const [twoFactor, setTwoFactor] = useState('')
  const [title, setTitle] = useState(null)
  const [description, setDescription] = useState(null)
  const [values, setValues] = useState(null)
  const [warning, setWarning] = useState(null)
  const [resolve, setResolve] = useState(null)
  const [reject, setReject] = useState(null)
  const [disableTwoFactor, setDisableTwoFactor] = useState(false)

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        cancel()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, cancel])

  useEffect(() => {
    console.log('twoFactor', twoFactor)
  }, [twoFactor])

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  useEffect(() => {
    if (!isOpen) {
      setTitle(null)
      setResolve(null)
      setReject(null)
      setTwoFactor('')
      setDisableTwoFactor(false)
    }
  }, [isOpen])

  const activate = ({ title, disableTwoFactor, description, values, warning }) => {
    console.log('activating')
    return new Promise((resolve, reject) => {
      setIsOpen(true)
      setDisableTwoFactor(disableTwoFactor)
      setTitle(title)
      setDescription(description)
      setValues(values)
      setWarning(warning)
      setResolve(() => resolve)
      setReject(() => reject)
      setTwoFactor('')
    })
  }

  const cancel = useCallback(async () => {
    if (!isOpen) return
    await clear()
    reject(new Error('[dialog-cancel]'))
  }, [isOpen, reject])

  const confirm = async () => {
    console.log('twoFactor: ', twoFactor)
    resolve(twoFactor)
    await clear()
  }

  const clear = () => {
    setIsOpen(false)
    setTitle(null)
    setResolve(null)
    setReject(null)
    setTwoFactor('')
    setDisableTwoFactor(false)
  }

  const renderValues = () => {
    return (values || []).map((value, index) => {
      return (
        <div key={index} className={styles.valueItem}>
          <div className={styles.label}>{value.label}</div>
          <div className={styles.value}>{value.value}</div>
        </div>
      )
    })
  }

  const renderUser = () => {
    if (!data || !data.me) return
    const { profile } = data.me
    return (
      <div className={styles.bottom}>
        <span className={styles.cancel} onClick={cancel}>
          x
        </span>
        <span className={styles.user}>Conectado como {profile.fullName}</span>
      </div>
    )
  }

  const renderTwoFactor = () => {
    if (disableTwoFactor) return
    if (!data || !data.me) return
    if (!data.me.hasTwoFactor) return
    return (
      <div className={styles.twoFactor}>
        <Form state={{ twoFactor }} onChange={changes => setTwoFactor(changes.twoFactor)}>
          <Field fieldName="twoFactor" type={TwoFactorButton} label="Confirmar" onClick={confirm} />
        </Form>
      </div>
    )
  }

  const renderButton = () => {
    if (!data || !data.me) return
    if (data.me.hasTwoFactor && !disableTwoFactor) return
    return (
      <div className="tds-orionx-btn h-flex-center-items" onClick={confirm}>
        <FaLock />
        &nbsp;&nbsp;
        <span>Confirmar</span>
      </div>
    )
  }

  const renderInfo = () => {
    return (
      <div className={styles.values}>
        {renderValues()}
        <div className={styles.separator} />
        <div className={styles.date}>{moment().format('LL')}</div>
      </div>
    )
  }

  const renderSmallInfo = () => {
    return (
      <div className={styles.smallValues}>
        {renderValues()}
        <div className={styles.separator} />
        <div className={styles.date}>{moment().format('LL')}</div>
      </div>
    )
  }

  const renderDialog = () => {
    if (!isOpen) return <span />
    return (
      <div className={`${styles.container}`}>
        <div className={styles.dialog}>
          <div className={styles.main}>
            <div className={styles.title}>{title}</div>
            <div className={styles.description}>{description}</div>
            {innerWidth > 600 ? null : renderSmallInfo()}
            <div className={styles.separator} />
            {renderTwoFactor()}
            {renderButton()}
            {renderUser()}
            {warning}
          </div>
          {innerWidth > 600 ? renderInfo() : null}
        </div>
        <div className={styles.overlay} onClick={cancel}></div>
      </div>
    )
  }

  return (
    <ConfirmContext.Provider value={{ confirm: activate }}>
      <Outlet context={props} />
      <div
        className={`transition-all duration-500 ${
          isOpen ? 'opacity-100 bg-gray-900' : 'opacity-0'
        }`}>
        {isOpen && renderDialog()}
      </div>
    </ConfirmContext.Provider>
  )
}

ConfirmationDialogComponent.propTypes = {
  children: PropTypes.node,
  innerWidth: PropTypes.number,
}

export const ConfirmationDialog = withSize(ConfirmationDialogComponent)
